/**
 * Light rain.
 *
 * A column of falling streaks that follows the camera, so a few hundred points
 * cover the whole visible world. Rain that is simulated across the map would
 * cost thousands of particles to look the same from any one place.
 *
 * Drawn as `Points` with a stretched sprite rather than as line segments: a
 * point sprite is one vertex per drop, and at this density the difference is
 * three hundred vertices against six hundred.
 */
import * as THREE from 'three';

const DROPS = 1500;
/** Half-width of the box the drops live in, centred on the camera. */
const SPAN = 20;
const TOP = 18;

export interface RainRig {
  group: THREE.Group;
  /** 0 = dry, 1 = full light rain. Eased by the caller's weather blend. */
  setIntensity(value: number): void;
  update(dt: number, cameraX: number, cameraZ: number): void;
}

export function createRain(): RainRig {
  const group = new THREE.Group();

  const positions = new Float32Array(DROPS * 3);
  const speeds = new Float32Array(DROPS);
  for (let i = 0; i < DROPS; i++) {
    positions[i * 3] = (Math.random() - 0.5) * SPAN * 2;
    positions[i * 3 + 1] = Math.random() * TOP;
    positions[i * 3 + 2] = (Math.random() - 0.5) * SPAN * 2;
    speeds[i] = 14 + Math.random() * 9;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const points = new THREE.Points(geometry, new THREE.PointsMaterial({
    // Rain reads as a darker streak against a bright sky and a pale pavement,
    // not as a white one: at 0.55 opacity in near-white it was invisible.
    color: 0x7d92a8,
    // A falling drop is a streak, and a point sprite is square: the size is
    // the streak's *length*, with the width coming from the texture.
    size: 0.55,
    map: streakTexture(),
    transparent: true,
    opacity: 0,
    depthWrite: false,
    sizeAttenuation: true,
    fog: false,
  }));
  points.frustumCulled = false;
  points.visible = false;
  group.add(points);

  let intensity = 0;

  return {
    group,
    setIntensity(value: number): void {
      intensity = Math.max(0, Math.min(1, value));
    },
    update(dt: number, cameraX: number, cameraZ: number): void {
      const material = points.material as THREE.PointsMaterial;
      material.opacity = intensity * 0.9;
      points.visible = intensity > 0.02;
      if (!points.visible) return;

      const array = geometry.attributes.position!.array as Float32Array;
      for (let i = 0; i < DROPS; i++) {
        const i3 = i * 3;
        array[i3 + 1]! -= speeds[i]! * dt;
        // A slight slant, so it is rain rather than a screensaver.
        array[i3]! += 2.2 * dt;
        if (array[i3 + 1]! < 0) {
          array[i3 + 1] = TOP;
        }
        // Wrap the column around the camera on both axes. Respawning only at
        // the bottom leaves the whole field behind wherever the player started
        // if they travel further than the column is wide - which, in a town
        // 200 m across, is most of the time.
        const dx = array[i3]! - cameraX;
        if (dx > SPAN) array[i3]! -= SPAN * 2;
        else if (dx < -SPAN) array[i3]! += SPAN * 2;
        const dz = array[i3 + 2]! - cameraZ;
        if (dz > SPAN) array[i3 + 2]! -= SPAN * 2;
        else if (dz < -SPAN) array[i3 + 2]! += SPAN * 2;
      }
      geometry.attributes.position!.needsUpdate = true;
    },
  };
}

/** A vertical streak, so each drop reads as falling rather than as a dot. */
function streakTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 12;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createLinearGradient(0, 0, 0, 64);
  gradient.addColorStop(0, 'rgba(255,255,255,0)');
  gradient.addColorStop(0.3, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(0.85, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(5, 0, 2, 64);
  return new THREE.CanvasTexture(canvas);
}
